/**
 * Error catalog of Argos (docs/ERRORES.md).
 *
 * Two identifiers, with different audiences (decision of David, 27/09/2026:
 * «arg y número, lo otro da mucha info»):
 *
 * - The REFERENCE (`ARG-48213907`) is the ONLY thing users, browsers and API
 *   clients ever see: «Se ha producido un error interno (ARG-48213907)». It
 *   identifies one occurrence; it is random (CSPRNG, 8 digits), not sequential
 *   and encodes nothing (no tenant, service, area or cause). PostgreSQL checks
 *   it for collisions and stores it (migration 2700) so support can look it up.
 * - The CODE (`E2001`) is INTERNAL: what failed, for Argos staff and the
 *   console only (never sent to browsers or API clients). Numeric, without
 *   area words; the area is a separate column. Stable: never reused or
 *   renumbered. A code is added here AND in the `error_catalog` rows of a
 *   migration (tests compare both, with and without a database).
 * - `description` and `action` are internal texts for Argos staff (Spanish,
 *   like the rest of the technical documentation); `title` is localized for
 *   the console.
 *
 * Dependency-free: the classifier recognises the error classes of the other
 * packages by their `name` and typed properties (duck typing), never by
 * their messages.
 */

export const ERROR_AREAS = [
  'llm',
  'mcp',
  'clickhouse',
  'postgres',
  'ubikos',
  'revo',
  'auth',
  'internal',
  'web',
] as const;
export type ErrorArea = (typeof ERROR_AREAS)[number];

export const ERROR_SEVERITIES = ['critical', 'error', 'warning'] as const;
export type ErrorSeverity = (typeof ERROR_SEVERITIES)[number];

/**
 * Services that record occurrences. The web and the console have no database:
 * they report theirs to the API (`POST /internal/v1/errors/report`, error-report-token.ts).
 */
export const ERROR_SERVICES = ['api', 'chat', 'mcp', 'worker', 'web', 'console'] as const;
export type ErrorService = (typeof ERROR_SERVICES)[number];

export const ERROR_LOCALES = ['es', 'ca', 'en', 'fr'] as const;
export type ErrorLocale = (typeof ERROR_LOCALES)[number];

/** Internal catalog code (console only). */
export const ERROR_CODE_RE = /^E\d{4}$/;
/** User-facing reference of one occurrence: `ARG-` + 8 random digits. */
export const ERROR_REF_RE = /^ARG-\d{8}$/;

/** Platform incident opened when `threshold` occurrences happen within `windowMinutes`. */
export interface ErrorIncidentRule {
  readonly threshold: number;
  readonly windowMinutes: number;
}

export interface ErrorCodeDefinition {
  readonly code: string;
  readonly area: ErrorArea;
  readonly severity: ErrorSeverity;
  /** Short title (Console › Errores, incidents). */
  readonly title: Readonly<Record<ErrorLocale, string>>;
  /** Internal description for Argos staff (Spanish). */
  readonly description: string;
  /** Suggested action for Argos staff (Spanish). */
  readonly action: string;
  /** Opens/updates a PLATFORM incident (Console › Incidencias); absent = never. */
  readonly incident?: ErrorIncidentRule;
}

const t = (es: string, ca: string, en: string, fr: string) => ({ es, ca, en, fr }) as const;

/**
 * The catalog. Same codes, areas, severities and incident rules as the
 * `error_catalog` rows of migration 2700 (compared by tests).
 */
export const ERROR_CATALOG = [
  // --- Internal ------------------------------------------------------------------
  {
    code: 'E1000',
    area: 'internal',
    severity: 'error',
    title: t(
      'Error interno inesperado',
      'Error intern inesperat',
      'Unexpected internal error',
      'Erreur interne inattendue',
    ),
    description:
      'Excepción no clasificada en un servicio (API, chat, MCP o worker). El detalle guardado es el nombre de la clase y el mensaje saneado.',
    action:
      'Buscar la referencia en los registros del servicio (campo `ref`) para ver la traza completa; si se repite, abrir una tarea con la corrección y, si procede, añadir un código específico al catálogo.',
  },
  {
    code: 'E1001',
    area: 'internal',
    severity: 'error',
    title: t(
      'Servicio interno inalcanzable',
      'Servei intern inabastable',
      'Internal service unreachable',
      'Service interne injoignable',
    ),
    description:
      'Un servicio no ha podido conectar con otro servicio interno (conexión rechazada, reiniciada o tiempo agotado): p. ej. la web con la API o el chat.',
    action:
      'Comprobar que los pods del servicio destino están en marcha (`kubectl get pods -A`), sus reinicios y las NetworkPolicies; revisar el despliegue automático reciente.',
  },
  {
    code: 'E1002',
    area: 'internal',
    severity: 'error',
    title: t(
      'Respuesta interna no válida',
      'Resposta interna no vàlida',
      'Invalid internal response',
      'Réponse interne invalide',
    ),
    description:
      'Un servicio interno ha devuelto una respuesta que no cumple el contrato (esquema, tamaño o formato): suele indicar versiones desalineadas entre servicios.',
    action:
      'Comprobar que todos los servicios ejecutan la misma versión de `main` (despliegue a medias) y revisar los registros del servicio que responde.',
  },
  // --- AI provider (llm-gateway) ------------------------------------------------------
  {
    code: 'E2001',
    area: 'llm',
    severity: 'critical',
    title: t(
      'Clave de OpenRouter sin saldo',
      "Clau d'OpenRouter sense saldo",
      'OpenRouter key out of credit',
      'Clé OpenRouter sans crédit',
    ),
    description:
      'El proveedor de IA rechaza la clave por límite de gasto de la clave o saldo agotado (HTTP 403 «Key limit exceeded» o HTTP 402). Afecta a todos los clientes: el chat, los resúmenes y los informes dejan de responder.',
    action:
      'Recargar saldo en OpenRouter o subir el límite de la clave (panel de OpenRouter › Keys); revisar el consumo en Consola › Consumo IA y la alerta diaria de gasto. La incidencia se resuelve sola cuando deja de repetirse.',
    incident: { threshold: 1, windowMinutes: 10 },
  },
  {
    code: 'E2002',
    area: 'llm',
    severity: 'error',
    title: t(
      'Proveedor de IA no disponible',
      "Proveïdor d'IA no disponible",
      'AI provider unavailable',
      "Fournisseur d'IA indisponible",
    ),
    description:
      'El proveedor de IA no responde o falla (error de red, HTTP 5xx, ningún endpoint con retención cero para el modelo, error a mitad de la respuesta).',
    action:
      'Consultar el estado de OpenRouter y del modelo; si el fallo es «sin endpoint ZDR», revisar el modelo de la ruta en Consola › Modelos IA (docs/IA-MODELOS.md).',
    incident: { threshold: 10, windowMinutes: 10 },
  },
  {
    code: 'E2003',
    area: 'llm',
    severity: 'warning',
    title: t(
      'Proveedor de IA limitando peticiones',
      "Proveïdor d'IA limitant peticions",
      'AI provider rate limiting',
      "Fournisseur d'IA limitant les requêtes",
    ),
    description: 'El proveedor de IA ha respondido HTTP 429 (límite de peticiones).',
    action:
      'Si es continuo, revisar el plan de OpenRouter y los límites por modelo; valorar un modelo de respaldo distinto en la ruta.',
  },
  {
    code: 'E2004',
    area: 'llm',
    severity: 'error',
    title: t(
      'Proveedor de IA rechaza la petición',
      "Proveïdor d'IA rebutja la petició",
      'AI provider rejected the request',
      "Fournisseur d'IA a refusé la requête",
    ),
    description:
      'El proveedor de IA ha rechazado la petición con un 4xx distinto de límite o clave (parámetros no admitidos por el modelo, petición demasiado grande…). No se reintenta con otro modelo.',
    action:
      'Revisar el modelo configurado para la tarea y sus parámetros admitidos (docs/IA-MODELOS.md); reproducir con `pnpm eval:llm` en el cliente de pruebas.',
  },
  {
    code: 'E2005',
    area: 'llm',
    severity: 'warning',
    title: t(
      'Presupuesto mensual de IA del cliente agotado',
      "Pressupost mensual d'IA del client exhaurit",
      "Client's monthly AI budget reached",
      'Budget IA mensuel du client atteint',
    ),
    description:
      'El cliente ha alcanzado su presupuesto mensual del asistente de IA (plan o excepción); el chat rechaza preguntas nuevas hasta el mes siguiente.',
    action:
      'Informar al MSP o al cliente; si procede, ampliar el límite con una excepción del plan (Consola › Clientes › Plan y uso).',
  },
  {
    code: 'E2006',
    area: 'llm',
    severity: 'critical',
    title: t(
      'Clave de OpenRouter no válida',
      "Clau d'OpenRouter no vàlida",
      'OpenRouter key invalid',
      'Clé OpenRouter invalide',
    ),
    description:
      'El proveedor de IA no acepta la clave (HTTP 401): revocada, rotada sin actualizar el secreto o mal copiada. Afecta a todos los clientes.',
    action:
      'Comprobar la clave en OpenRouter y el Secret del chat (docs/CUSTODIA-CLAVES.md); rotarla y reiniciar el chat.',
    incident: { threshold: 1, windowMinutes: 10 },
  },
  {
    code: 'E2007',
    area: 'llm',
    severity: 'warning',
    title: t(
      'Tiempo de respuesta de la IA agotado',
      'Temps de resposta de la IA exhaurit',
      'AI response timed out',
      'Délai de réponse de l’IA dépassé',
    ),
    description:
      'La llamada al modelo o el turno completo del chat han superado su tiempo máximo (proveedor lento o pregunta que necesita demasiadas consultas).',
    action:
      'Si se repite, revisar la latencia del modelo en OpenRouter y el tiempo por turno del chat; valorar un modelo más rápido para la tarea.',
  },
  {
    code: 'E2008',
    area: 'llm',
    severity: 'error',
    title: t(
      'Respuesta de la IA no válida',
      'Resposta de la IA no vàlida',
      'Invalid AI response',
      "Réponse de l'IA invalide",
    ),
    description:
      'La respuesta del proveedor no cumple el esquema esperado (JSON, llamadas a herramientas, tamaño).',
    action:
      'Revisar si el modelo de la ruta cambió de comportamiento; reproducir con la evaluación.',
  },
  {
    code: 'E2009',
    area: 'llm',
    severity: 'critical',
    title: t(
      'Configuración de IA no válida',
      "Configuració d'IA no vàlida",
      'Invalid AI configuration',
      "Configuration de l'IA invalide",
    ),
    description:
      'La configuración de la pasarela de IA no es válida (URL base, formato de la clave, proveedores permitidos).',
    action: 'Revisar las variables y los Secrets del chat (apps/chat/README.md).',
  },
  // Balance of the provider's key (migration 3400): no occurrences; the chat's
  // balance check opens the incident at its level and closes it on recovery.
  {
    code: 'E2010',
    area: 'llm',
    severity: 'warning',
    title: t(
      'Saldo del proveedor de IA por debajo del umbral',
      "Saldo del proveïdor d'IA per sota del llindar",
      'AI provider balance below threshold',
      "Solde du fournisseur d'IA sous le seuil",
    ),
    description:
      'La clave de OpenRouter ha gastado el 80 % o más de su límite (consulta periódica del chat a `GET /api/v1/key`, cada 15 min o a petición desde Consola › Consumo IA). Todavía funciona, pero se agotará pronto y entonces el chat, los resúmenes y los informes dejarán de responder (E2001).',
    action:
      'Subir el límite de la clave o recargar saldo en OpenRouter (panel › Keys) y pulsar «Actualizar» en Consola › Consumo IA; revisar el consumo por tarea (las evaluaciones cuentan como `eval`). La incidencia se cierra sola cuando el saldo vuelve por debajo del 80 %.',
  },
  {
    code: 'E2011',
    area: 'llm',
    severity: 'critical',
    title: t(
      'Saldo del proveedor de IA casi agotado',
      "Saldo del proveïdor d'IA gairebé exhaurit",
      'AI provider balance almost exhausted',
      "Solde du fournisseur d'IA presque épuisé",
    ),
    description:
      'La clave de OpenRouter ha gastado el 95 % o más de su límite (o el límite es 0). En cuanto llegue al 100 % el proveedor rechazará todas las peticiones (E2001) y la IA dejará de funcionar para todos los clientes.',
    action:
      'Subir el límite de la clave o recargar saldo en OpenRouter de inmediato y pulsar «Actualizar» en Consola › Consumo IA; parar evaluaciones en curso. La incidencia se cierra sola cuando el saldo baja del 95 % (pasa a E2010 si sigue por encima del 80 %).',
  },
  // --- MCP (tools of the chat) ------------------------------------------------------
  {
    code: 'E3001',
    area: 'mcp',
    severity: 'error',
    title: t(
      'Herramientas de datos no disponibles',
      'Eines de dades no disponibles',
      'Data tools unavailable',
      'Outils de données indisponibles',
    ),
    description:
      'El chat no ha podido usar el servidor MCP (caído, tiempo agotado o error interno de una herramienta de consulta).',
    action:
      'Comprobar el pod del MCP y sus registros (misma `ref`); si falla ClickHouse, ver los códigos E4001–E4004.',
  },
  {
    code: 'E3002',
    area: 'mcp',
    severity: 'error',
    title: t(
      'Respuesta del MCP no válida',
      'Resposta del MCP no vàlida',
      'Invalid MCP response',
      'Réponse MCP invalide',
    ),
    description: 'El servidor MCP ha devuelto una respuesta que no cumple el protocolo esperado.',
    action: 'Comprobar que el chat y el MCP ejecutan la misma versión.',
  },
  // --- ClickHouse -----------------------------------------------------------------------
  {
    code: 'E4001',
    area: 'clickhouse',
    severity: 'critical',
    title: t(
      'ClickHouse no disponible',
      'ClickHouse no disponible',
      'ClickHouse unavailable',
      'ClickHouse indisponible',
    ),
    description:
      'No se puede conectar con ClickHouse (conexión rechazada o cortada): los paneles, las métricas y el chat se quedan sin datos.',
    action: 'Revisar el pod de `argos-clickhouse`, su disco y memoria (deploy/platform/README.md).',
    incident: { threshold: 5, windowMinutes: 10 },
  },
  {
    code: 'E4002',
    area: 'clickhouse',
    severity: 'error',
    title: t(
      'ClickHouse: límite de memoria',
      'ClickHouse: límit de memòria',
      'ClickHouse: memory limit',
      'ClickHouse : limite de mémoire',
    ),
    description: 'Una consulta ha superado el límite de memoria de ClickHouse (código 241).',
    action:
      'Identificar la consulta (métrica, periodo, desglose) por la referencia; revisar los límites del usuario de lectura y la consulta de la capa semántica.',
  },
  {
    code: 'E4003',
    area: 'clickhouse',
    severity: 'error',
    title: t(
      'ClickHouse: tiempo agotado',
      'ClickHouse: temps exhaurit',
      'ClickHouse: timeout',
      'ClickHouse : délai dépassé',
    ),
    description:
      'Una consulta a ClickHouse ha superado su tiempo máximo (códigos 159/160 o tiempo del cliente).',
    action: 'Revisar la carga de ClickHouse y la consulta; valorar un periodo o desglose menor.',
  },
  {
    code: 'E4004',
    area: 'clickhouse',
    severity: 'error',
    title: t(
      'ClickHouse: error de consulta',
      'ClickHouse: error de consulta',
      'ClickHouse: query error',
      'ClickHouse : erreur de requête',
    ),
    description: 'ClickHouse ha rechazado una consulta por un motivo no clasificado.',
    action: 'Ver el código de ClickHouse en el detalle y la consulta en `system.query_log`.',
  },
  // --- PostgreSQL -----------------------------------------------------------------------
  {
    code: 'E5001',
    area: 'postgres',
    severity: 'critical',
    title: t(
      'PostgreSQL no disponible',
      'PostgreSQL no disponible',
      'PostgreSQL unavailable',
      'PostgreSQL indisponible',
    ),
    description:
      'No se puede conectar con PostgreSQL o no admite más conexiones. Mientras dure, las ocurrencias no se pueden guardar: quedan solo en los registros.',
    action: 'Revisar el pod de `argos-postgres`, las conexiones (`pg_stat_activity`) y el disco.',
  },
  {
    code: 'E5002',
    area: 'postgres',
    severity: 'error',
    title: t(
      'PostgreSQL: tiempo agotado',
      'PostgreSQL: temps exhaurit',
      'PostgreSQL: timeout',
      'PostgreSQL : délai dépassé',
    ),
    description: 'Una sentencia ha superado `statement_timeout` (57014).',
    action: 'Buscar la sentencia lenta (`pg_stat_statements`) y los bloqueos.',
  },
  {
    code: 'E5003',
    area: 'postgres',
    severity: 'warning',
    title: t(
      'PostgreSQL: conflicto de concurrencia',
      'PostgreSQL: conflicte de concurrència',
      'PostgreSQL: concurrency conflict',
      'PostgreSQL : conflit de concurrence',
    ),
    description: 'Interbloqueo o fallo de serialización (40P01/40001); reintentar suele bastar.',
    action: 'Si se repite, revisar el orden de bloqueo de la operación indicada.',
  },
  {
    code: 'E5004',
    area: 'postgres',
    severity: 'error',
    title: t(
      'PostgreSQL: error inesperado',
      'PostgreSQL: error inesperat',
      'PostgreSQL: unexpected error',
      'PostgreSQL : erreur inattendue',
    ),
    description:
      'PostgreSQL ha rechazado una sentencia con un código no previsto por la operación (restricción, tipo, permiso…). Solo se guarda el SQLSTATE.',
    action: 'Ver el SQLSTATE del detalle y la traza en los registros del servicio (misma `ref`).',
  },
  // --- Connectors -----------------------------------------------------------------------
  {
    code: 'E6001',
    area: 'ubikos',
    severity: 'error',
    title: t(
      'Ubikos rechaza la clave',
      'Ubikos rebutja la clau',
      'Ubikos rejected the key',
      'Ubikos a refusé la clé',
    ),
    description: 'Ubikos responde 401/403 o la credencial no está disponible en el almacén.',
    action:
      'Pedir al cliente (vía MSP) que confirme la API key y cambiarla en Consola › Clientes › conexiones.',
  },
  {
    code: 'E6002',
    area: 'ubikos',
    severity: 'error',
    title: t(
      'Ubikos cambió el formato',
      'Ubikos ha canviat el format',
      'Ubikos changed its format',
      'Ubikos a changé de format',
    ),
    description:
      'La respuesta de Ubikos no cumple el esquema validado (campo nuevo, tipo distinto…): no se ingiere nada hasta revisarlo.',
    action:
      'Ver los campos en Operaciones (ejecución fallida), clasificarlos en el conector (packages/connector-ubikos/README.md) y relanzar.',
  },
  {
    code: 'E6003',
    area: 'ubikos',
    severity: 'warning',
    title: t(
      'Ubikos no responde',
      'Ubikos no respon',
      'Ubikos not responding',
      'Ubikos ne répond pas',
    ),
    description: 'Tiempo agotado o error de red al llamar a Ubikos (tras agotar los reintentos).',
    action:
      'Comprobar el estado de Ubikos y el proxy de salida (`argos-egress`, perfil connectors).',
  },
  {
    code: 'E6004',
    area: 'ubikos',
    severity: 'error',
    title: t(
      'Ubikos devolvió un error',
      'Ubikos ha retornat un error',
      'Ubikos returned an error',
      'Ubikos a renvoyé une erreur',
    ),
    description: 'Ubikos ha respondido con un error HTTP (4xx/5xx) distinto de clave o tiempo.',
    action: 'Ver el estado HTTP y el endpoint en el detalle y en Operaciones.',
  },
  {
    code: 'E6005',
    area: 'ubikos',
    severity: 'error',
    title: t(
      'Error inesperado en la sincronización de Ubikos',
      'Error inesperat en la sincronització d’Ubikos',
      'Unexpected Ubikos sync error',
      'Erreur inattendue de synchronisation Ubikos',
    ),
    description: 'La sincronización de Ubikos ha fallado por un motivo no clasificado.',
    action: 'Ver la ejecución en Operaciones y los registros del worker (misma `ref`).',
  },
  {
    code: 'E6101',
    area: 'revo',
    severity: 'error',
    title: t(
      'Revo rechaza la autorización',
      "Revo rebutja l'autorització",
      'Revo rejected the authorization',
      "Revo a refusé l'autorisation",
    ),
    description: 'Revo responde 401/403, el token OAuth caducó o la credencial no está disponible.',
    action: 'Pedir al cliente que vuelva a autorizar Revo desde Conexiones.',
  },
  {
    code: 'E6102',
    area: 'revo',
    severity: 'error',
    title: t(
      'Revo cambió el formato',
      'Revo ha canviat el format',
      'Revo changed its format',
      'Revo a changé de format',
    ),
    description: 'La respuesta de Revo no cumple el esquema validado: no se ingiere nada.',
    action: 'Ver los campos en Operaciones y revisarlos en packages/connector-revo.',
  },
  {
    code: 'E6103',
    area: 'revo',
    severity: 'warning',
    title: t('Revo no responde', 'Revo no respon', 'Revo not responding', 'Revo ne répond pas'),
    description: 'Tiempo agotado o error de red al llamar a Revo (tras agotar los reintentos).',
    action: 'Comprobar el estado de Revo y el proxy de salida.',
  },
  {
    code: 'E6104',
    area: 'revo',
    severity: 'error',
    title: t(
      'Revo devolvió un error',
      'Revo ha retornat un error',
      'Revo returned an error',
      'Revo a renvoyé une erreur',
    ),
    description: 'Revo ha respondido con un error HTTP distinto de autorización o tiempo.',
    action: 'Ver el estado HTTP y el endpoint en el detalle y en Operaciones.',
  },
  {
    code: 'E6105',
    area: 'revo',
    severity: 'error',
    title: t(
      'Error inesperado en la sincronización de Revo',
      'Error inesperat en la sincronització de Revo',
      'Unexpected Revo sync error',
      'Erreur inattendue de synchronisation Revo',
    ),
    description: 'La sincronización de Revo ha fallado por un motivo no clasificado.',
    action: 'Ver la ejecución en Operaciones y los registros del worker.',
  },
  // --- Identity ---------------------------------------------------------------------
  {
    code: 'E7001',
    area: 'auth',
    severity: 'critical',
    title: t(
      'Proveedor de identidad no disponible',
      "Proveïdor d'identitat no disponible",
      'Identity provider unavailable',
      "Fournisseur d'identité indisponible",
    ),
    description:
      'Zitadel (oculto) no responde a la web, la consola o la API: nadie puede entrar ni gestionar usuarios.',
    action: 'Revisar el pod de `argos-identity` y su base de datos (deploy/platform/README.md).',
  },
  // --- Web / console rendering ------------------------------------------------------
  {
    code: 'E8001',
    area: 'web',
    severity: 'error',
    title: t(
      'Error al mostrar la página',
      'Error en mostrar la pàgina',
      'Page failed to render',
      "Erreur d'affichage de la page",
    ),
    description:
      'Excepción no controlada al generar una página de la web o de la consola. Solo queda en los registros (la web y la consola no escriben en PostgreSQL).',
    action:
      'Buscar la referencia en los registros de la web o la consola (`ref`); la traza completa está junto a ella.',
  },
] as const satisfies readonly ErrorCodeDefinition[];

export type ErrorCode = (typeof ERROR_CATALOG)[number]['code'];

export const ERROR_CODES: readonly ErrorCode[] = ERROR_CATALOG.map((entry) => entry.code);

/** Fallback code of anything unclassified. */
export const UNEXPECTED_ERROR: ErrorCode = 'E1000';

const BY_CODE: ReadonlyMap<string, ErrorCodeDefinition> = new Map(
  ERROR_CATALOG.map((entry) => [entry.code, entry as ErrorCodeDefinition]),
);

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && BY_CODE.has(value);
}

export function errorDefinition(code: ErrorCode): ErrorCodeDefinition {
  // Every ErrorCode is in the map (built from the same list).
  const definition = BY_CODE.get(code);
  if (definition === undefined) {
    throw new TypeError('Unknown error code');
  }
  return definition;
}

export function isErrorService(value: unknown): value is ErrorService {
  return typeof value === 'string' && (ERROR_SERVICES as readonly string[]).includes(value);
}

export function isErrorRef(value: unknown): value is string {
  return typeof value === 'string' && ERROR_REF_RE.test(value);
}

/** Largest multiple of 10^8 below 2^32: rejection sampling keeps the digits uniform. */
const REF_LIMIT = 4_200_000_000;

/**
 * A new occurrence reference: `ARG-` + 8 digits from the CSPRNG (uniform,
 * not sequential, no meaning). PostgreSQL re-checks uniqueness when storing it
 * and may answer another one (collision): use the reference it returns.
 */
export function newErrorRef(): string {
  const word = new Uint32Array(1);
  for (;;) {
    globalThis.crypto.getRandomValues(word);
    const value = word[0] ?? REF_LIMIT;
    if (value < REF_LIMIT) {
      return `ARG-${String(value % 100_000_000).padStart(8, '0')}`;
    }
  }
}

/**
 * Deterministic reference of a Next.js error `digest` (same value in the
 * server log and in the browser's error page, which only knows the digest).
 * The digest is already an opaque hash; this only gives it the reference
 * format. The BFF reports it to the API like its other references
 * (docs/ERRORES.md §4.1); the same digest gives the same reference, so a
 * repeated failure may be stored under a new one (collision) while the page
 * keeps showing the first, which still finds the same code and route.
 */
export function refFromDigest(digest: string): string {
  const fnv = (seed: number) => {
    let hash = seed >>> 0;
    for (let index = 0; index < digest.length; index++) {
      hash ^= digest.charCodeAt(index) & 0xffff;
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash;
  };
  const value = (fnv(0x811c9dc5) % 10_000) * 10_000 + (fnv(0x050c5d1f) % 10_000);
  return `ARG-${String(value).padStart(8, '0')}`;
}

// ---------------------------------------------------------------------------
// Sanitisation of the stored detail (and of anything logged with a code)
// ---------------------------------------------------------------------------

export const MAX_ERROR_DETAIL = 500;

const SANITIZERS: readonly (readonly [RegExp, string])[] = [
  // Credentials inside URLs (scheme://user:password@host).
  [/\b([a-z][a-z0-9+.-]{1,20}:\/\/)[^\s/@]+@/gi, '$1[redacted]@'],
  // Query strings and fragments of URLs (tokens, codes, e-mails travel there).
  [/(\b[a-z][a-z0-9+.-]{1,20}:\/\/[^\s?#]*)[?#][^\s]*/gi, '$1'],
  // Authorization schemes.
  [/\b(bearer|basic|token)\s+[A-Za-z0-9._~+/=-]{8,}/gi, '$1 [redacted]'],
  // JSON Web Tokens.
  [/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g, '[token]'],
  // Provider API keys (OpenRouter `sk-or-v1-…`, OpenAI-like `sk-…`).
  [/\bsk-[A-Za-z0-9_-]{12,}/g, '[redacted]'],
  // key=value / "key": "value" / key: value for sensitive keys.
  [
    /\b(password|passwd|passphrase|secret|client_secret|token|access_token|refresh_token|api[_-]?key|apikey|authorization|cookie|private_key|credentials?)(["']?\s*[:=]\s*["']?)[^\s"',;&}]+/gi,
    '$1$2[redacted]',
  ],
  // E-mail addresses (personal data).
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
  // IPv4 addresses (infrastructure details).
  [/\b\d{1,3}(?:\.\d{1,3}){3}(?::\d{1,5})?\b/g, '[ip]'],
  // Long opaque strings (keys, tokens, hashes): 32+ base64/hex characters, or
  // 40+ with dashes (a UUID, 36, is kept; paths are split by `/`).
  [/[A-Za-z0-9+_]{32,}={0,2}/g, '[redacted]'],
  [/[A-Za-z0-9_-]{40,}/g, '[redacted]'],
  // Runs of 9+ digits (phone, card or account numbers).
  [/(?<![\w-])\d[\d ]{7,}\d(?![\w-])/g, '[number]'],
];

/**
 * Detail safe to store and to show to Argos staff: control characters
 * removed, credentials, tokens, e-mails, IPs and long opaque strings masked,
 * at most 500 characters. Defence in depth: callers should build details
 * from typed properties, not from free text.
 */
export function sanitizeErrorDetail(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  // eslint-disable-next-line no-control-regex
  let text = value.replace(/[\u0000-\u001f\u007f]+/g, ' ');
  for (const [pattern, replacement] of SANITIZERS) {
    text = text.replace(pattern, replacement);
  }
  text = text.replace(/\s{2,}/g, ' ').trim();
  if (text === '') {
    return null;
  }
  return text.length > MAX_ERROR_DETAIL ? `${text.slice(0, MAX_ERROR_DETAIL - 1)}…` : text;
}

// ---------------------------------------------------------------------------
// Classification of the error classes of the other packages (duck typing)
// ---------------------------------------------------------------------------

export interface ErrorClassification {
  readonly code: ErrorCode;
  /** Safe detail built from typed properties only (never provider bodies). */
  readonly detail: string;
}

const prop = (error: unknown, key: string): unknown =>
  typeof error === 'object' && error !== null ? (error as Record<string, unknown>)[key] : undefined;
const str = (value: unknown, re: RegExp): string | undefined =>
  typeof value === 'string' && re.test(value) ? value : undefined;
const int = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < 1000
    ? value
    : undefined;

const NAME_RE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
const TOKEN_RE = /^[a-z][a-z0-9_]{0,39}$/;
const SYSCODE_RE = /^[A-Z][A-Z0-9_]{1,39}$/;
const SQLSTATE_RE = /^[0-9A-Z]{5}$/;

function nameOf(error: unknown): string {
  const name = prop(error, 'name');
  const ctor =
    typeof error === 'object' && error !== null
      ? (error as { constructor?: { name?: unknown } }).constructor?.name
      : undefined;
  // Classes that keep the default `name` ('Error') are recognised by their constructor.
  if (typeof ctor === 'string' && NAME_RE.test(ctor) && ctor !== 'Error' && ctor !== 'Object') {
    return ctor;
  }
  return typeof name === 'string' && NAME_RE.test(name) ? name : 'Error';
}

/** Code of a gateway error (`LlmError`: code, HTTP status and credential reason). */
export function llmErrorCodeOf(llmCode: unknown, status?: unknown, reason?: unknown): ErrorCode {
  if (reason === 'key_limit' || reason === 'credits_exhausted') {
    return 'E2001';
  }
  if (reason === 'invalid_key' || status === 401) {
    return 'E2006';
  }
  switch (llmCode) {
    case 'rate_limited':
      return 'E2003';
    case 'timeout':
      return 'E2007';
    case 'unavailable':
      return 'E2002';
    case 'rejected':
      return 'E2004';
    case 'bad_response':
      return 'E2008';
    case 'config':
      return 'E2009';
    default:
      return 'E2002';
  }
}

const PG_UNAVAILABLE = new Set(['53300', '53400', '57P01', '57P02', '57P03']);
const NETWORK_CODES = new Set([
  'ECONNREFUSED',
  'ECONNRESET',
  'EPIPE',
  'ETIMEDOUT',
  'EHOSTUNREACH',
  'ENETUNREACH',
  'ENOTFOUND',
  'EAI_AGAIN',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_SOCKET',
]);

/** Connector sync failure (worker): the class of the operations registry → code. */
export function connectorErrorCodeOf(connector: string, errorClass: string): ErrorCode | null {
  const base = connector === 'ubikos' ? 6000 : connector === 'revo' ? 6100 : null;
  if (base === null) {
    return null;
  }
  const offset =
    errorClass === 'auth'
      ? 1
      : errorClass === 'schema'
        ? 2
        : errorClass === 'timeout' || errorClass === 'network'
          ? 3
          : errorClass === 'http'
            ? 4
            : 5;
  const code = `E${String(base + offset)}`;
  return isErrorCode(code) ? code : null;
}

/**
 * Code and safe detail of any thrown value. Recognises, by name and typed
 * properties: LlmError, McpError, ClickHouse errors, PostgreSQL (`pg`)
 * errors, the connector errors, Zitadel errors and low-level network errors.
 * Anything else is E1000 with the class name only (never its message:
 * it may carry SQL, values or personal data).
 */
export function classifyError(error: unknown): ErrorClassification {
  const name = nameOf(error);
  const tagged = prop(error, 'argosErrorCode');
  if (isErrorCode(tagged)) {
    return { code: tagged, detail: name };
  }
  const code = prop(error, 'code');
  const status = int(prop(error, 'status'));

  if (name === 'LlmError') {
    const llmCode = str(code, TOKEN_RE);
    const reason = str(prop(error, 'reason'), TOKEN_RE);
    return {
      code: llmErrorCodeOf(llmCode, status, reason),
      detail: [
        'LlmError',
        llmCode,
        status !== undefined ? `status=${String(status)}` : undefined,
        reason !== undefined ? `reason=${reason}` : undefined,
      ]
        .filter(Boolean)
        .join(' '),
    };
  }
  if (name === 'McpError') {
    const mcpCode = str(code, TOKEN_RE);
    return {
      code: mcpCode === 'bad_response' ? 'E3002' : 'E3001',
      detail: `McpError ${mcpCode ?? 'unknown'}`,
    };
  }
  if (name === 'ClickHouseError') {
    const chCode = str(code, /^\d{1,4}$/);
    const type = str(prop(error, 'type'), SYSCODE_RE);
    const detail = ['ClickHouseError', chCode, type].filter(Boolean).join(' ');
    if (chCode === '241') return { code: 'E4002', detail };
    if (chCode === '159' || chCode === '160') return { code: 'E4003', detail };
    if (chCode === '210' || chCode === '209') return { code: 'E4001', detail };
    return { code: 'E4004', detail };
  }
  const sqlstate = str(code, SQLSTATE_RE);
  if (sqlstate !== undefined && typeof prop(error, 'severity') === 'string') {
    const detail = `PostgreSQL ${sqlstate}`;
    if (sqlstate.startsWith('08') || PG_UNAVAILABLE.has(sqlstate)) {
      return { code: 'E5001', detail };
    }
    if (sqlstate === '57014') return { code: 'E5002', detail };
    if (sqlstate === '40001' || sqlstate === '40P01') return { code: 'E5003', detail };
    return { code: 'E5004', detail };
  }
  if (name.startsWith('Ubikos') || name.startsWith('Revo')) {
    const connector = name.startsWith('Ubikos') ? 'ubikos' : 'revo';
    const errorClass = name.endsWith('SchemaError')
      ? 'schema'
      : name.endsWith('AuthorizationError') || status === 401 || status === 403
        ? 'auth'
        : name.endsWith('NetworkError') || status === 408 || status === 504
          ? 'timeout'
          : name.endsWith('HttpError')
            ? 'http'
            : 'unknown';
    return {
      code: connectorErrorCodeOf(connector, errorClass) ?? UNEXPECTED_ERROR,
      detail: [name, status !== undefined ? `status=${String(status)}` : undefined]
        .filter(Boolean)
        .join(' '),
    };
  }
  if (name === 'ZitadelError' || name === 'UnavailableError') {
    return { code: 'E7001', detail: name };
  }
  const message = prop(error, 'message');
  // ClickHouse client timeout (plain Error) and pg pool/connection failures.
  if (typeof message === 'string') {
    if (message === 'Timeout error.') {
      return { code: 'E4003', detail: 'ClickHouse client timeout' };
    }
    if (
      /^(timeout exceeded when trying to connect|Connection terminated unexpectedly)/.test(message)
    ) {
      return { code: 'E5001', detail: 'PostgreSQL connection' };
    }
  }
  const syscode = str(code, SYSCODE_RE);
  if (syscode !== undefined && NETWORK_CODES.has(syscode)) {
    return { code: 'E1001', detail: `${name} ${syscode}` };
  }
  if (name === 'TimeoutError' || name === 'AbortError') {
    return { code: 'E1001', detail: name };
  }
  return { code: UNEXPECTED_ERROR, detail: name };
}

// ---------------------------------------------------------------------------
// Reporting contract (implemented by @argos/db; the web and console report to the API)
// ---------------------------------------------------------------------------

/** One user-facing failure to record. `tenantId` only from the verified identity. */
export interface ErrorOccurrence {
  readonly code: ErrorCode;
  /** Proposed reference (`newErrorRef()`); the recorder answers the stored one. */
  readonly ref: string;
  /** Verified tenant of the request, or null (platform-level: console, worker platform tasks). */
  readonly tenantId: string | null;
  /** Route pattern or operation name (never a URL with parameters or a query string). */
  readonly operation?: string;
  readonly httpStatus?: number;
  /** Built from typed properties; sanitised again before storing. */
  readonly detail?: string;
  /**
   * Milliseconds since the epoch when the user got the reference, for
   * occurrences recorded later than they happened (BFF reports buffered while
   * the API was unreachable). Absent = now.
   */
  readonly occurredAt?: number;
}

/**
 * Records occurrences. `report` never rejects: it resolves with the reference
 * to show (the one PostgreSQL stored, which differs from the proposed one only
 * after a collision; the proposed one if the database is slow or down).
 */
export interface ErrorReporter {
  report(occurrence: ErrorOccurrence): Promise<string>;
}

export const NO_ERROR_REPORTER: ErrorReporter = Object.freeze({
  report: (occurrence: ErrorOccurrence) => Promise.resolve(occurrence.ref),
});

/**
 * Body of a user-facing internal failure of the Fastify services: the
 * reference only (never the internal code, the area or any message).
 */
export interface InternalErrorBody {
  readonly statusCode: number;
  readonly error: 'internal';
  readonly ref: string;
}
